import { NavLink, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import {
  LayoutGrid, Trophy, GraduationCap, Zap, Sparkles, UsersRound,
  MessageSquare, Bell, BadgeCheck, LogOut, LineChart, ShieldAlert, User
} from "lucide-react";
import AmbientBackground from "./AmbientBackground";

const navItems = [
  { to: "/feed", label: "Feed", icon: LayoutGrid, testid: "nav-feed" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/lessons", label: "Lessons", icon: GraduationCap, testid: "nav-lessons" },
  { to: "/challenges", label: "Challenges", icon: Zap, testid: "nav-challenges" },
  { to: "/coach", label: "AI Coach", icon: Sparkles, testid: "nav-coach" },
  { to: "/mentors", label: "Mentors", icon: BadgeCheck, testid: "nav-mentors" },
  { to: "/communities", label: "Communities", icon: UsersRound, testid: "nav-communities" },
  { to: "/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the mobile menu when tapping outside.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [menuOpen]);

  return (
    <div className="relative min-h-screen bg-[#0A0A0A] text-[#F2F2F2] overflow-x-hidden">
      <AmbientBackground />

      {/* Mobile top bar (logo + avatar → user menu with Sign out) */}
      {user && (
        <header
          className="md:hidden sticky top-0 z-40 backdrop-blur-md bg-[#0A0A0A]/85 border-b border-white/5"
          data-testid="mobile-topbar"
        >
          <div className="flex items-center justify-between px-3 py-2.5">
            <button
              data-testid="mobile-logo-home"
              onClick={() => navigate("/feed")}
              className="flex items-center gap-2"
            >
              <div className="w-7 h-7 rounded-md bg-[#00E676] flex items-center justify-center">
                <LineChart size={15} strokeWidth={2.5} className="text-black" />
              </div>
              <span className="font-heading font-bold tracking-tight">Cashflow</span>
            </button>

            <div className="relative" ref={menuRef}>
              <button
                data-testid="mobile-user-menu-btn"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 bg-white/[0.04] border border-white/8 active:bg-white/[0.08]"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <img
                  src={user.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${user.user_id}`}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover bg-white/5"
                />
                <span className="text-[11px] font-mono text-white/70 max-w-[80px] truncate">
                  {user.name?.split(" ")[0]}
                </span>
              </button>

              {menuOpen && (
                <div
                  data-testid="mobile-user-menu"
                  role="menu"
                  className="absolute right-0 top-full mt-2 w-56 surface-elev rounded-lg border border-white/8 shadow-xl overflow-hidden z-50"
                >
                  <div className="px-3 py-2.5 border-b border-white/5">
                    <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                      {user.name}
                      {user.is_admin && (
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30 text-red-300">
                          admin
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#00E676] font-mono truncate">
                      ${(user.total_earnings || 0).toLocaleString()} earned
                    </div>
                  </div>
                  <button
                    data-testid="mobile-menu-profile"
                    onClick={() => { setMenuOpen(false); navigate(`/profile/${user.user_id}`); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-white/85 hover:bg-white/[0.05] active:bg-white/[0.08] text-left"
                  >
                    <User size={15} /> Profile
                  </button>
                  {user.is_admin && (
                    <button
                      data-testid="mobile-menu-admin"
                      onClick={() => { setMenuOpen(false); navigate("/admin"); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-300 hover:bg-red-500/10 active:bg-red-500/15 text-left"
                    >
                      <ShieldAlert size={15} /> Admin
                    </button>
                  )}
                  <button
                    data-testid="mobile-menu-logout"
                    onClick={() => { setMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-white/85 hover:bg-white/[0.05] active:bg-white/[0.08] text-left border-t border-white/5"
                  >
                    <LogOut size={15} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-60 bg-[#0A0A0A] border-r border-white/5 flex-col justify-between py-6 px-4 z-30">
        <div>
          <button
            data-testid="logo-home"
            onClick={() => navigate("/feed")}
            className="flex items-center gap-2.5 mb-8 group px-2"
          >
            <div className="w-8 h-8 rounded-md bg-[#00E676] flex items-center justify-center">
              <LineChart size={18} strokeWidth={2.5} className="text-black" />
            </div>
            <span className="font-heading font-bold text-xl tracking-tight">Cashflow</span>
          </button>

          <nav className="flex flex-col gap-0.5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-white/[0.06] text-white"
                      : "text-white/55 hover:text-white hover:bg-white/[0.03]"
                  }`
                }
              >
                <item.icon size={16} strokeWidth={2} />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
            {user?.is_admin && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors mt-2 border-t border-white/5 pt-3 ${
                    isActive
                      ? "bg-red-500/10 text-red-300"
                      : "text-red-300/70 hover:text-red-300 hover:bg-red-500/5"
                  }`
                }
              >
                <ShieldAlert size={16} strokeWidth={2} />
                <span className="font-medium">Admin</span>
              </NavLink>
            )}
          </nav>
        </div>

        {user && (
          <div className="space-y-2">
            <button
              data-testid="nav-profile"
              onClick={() => navigate(`/profile/${user.user_id}`)}
              className="w-full flex items-center gap-3 p-2.5 rounded-md hover:bg-white/[0.04] transition"
            >
              <img
                src={user.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${user.user_id}`}
                alt=""
                className="w-9 h-9 rounded-md object-cover bg-white/5"
              />
              <div className="text-left flex-1 min-w-0">
                <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                  {user.name}
                  {user.is_admin && <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30 text-red-300">admin</span>}
                </div>
                <div className="text-[11px] text-[#00E676] font-mono truncate">
                  ${(user.total_earnings || 0).toLocaleString()}
                </div>
              </div>
            </button>
            <button
              data-testid="nav-logout"
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-white/40 hover:text-white transition"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        )}
      </aside>

      {/* Mobile bottom nav */}
      <nav className="fixed md:hidden bottom-3 left-1/2 -translate-x-1/2 w-[94%] surface-elev rounded-full px-3 py-2 flex justify-between items-center z-50">
        {navItems.slice(0, 5).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`${item.testid}-mobile`}
            className={({ isActive }) =>
              `p-2.5 rounded-full transition ${isActive ? "bg-[#00E676] text-black" : "text-white/55"}`
            }
          >
            <item.icon size={18} strokeWidth={2} />
          </NavLink>
        ))}
      </nav>

      <main className="md:ml-60 relative z-10 pb-24 md:pb-12">{children}</main>
    </div>
  );
}
